/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
 *      1       | 23/11/2023 | Lic. José Luis Aragón Cervantes  | Creación
 *      2       | 13/08/2026 | Lic. José Luis Aragón Cervantes  | Soporte de lista completa sin paginado
 *      3       | 25/09/2026 | LIC. José Luis Aragón C.                       | Cantidad sin spinners/stepper;
 *              |            |                                  | navegación â†‘â†“â†→ en cuadrícula
 * ---------------------------------------------------------------------------------------
 */

import TextField from "../ui/TextField";
import SizeSoftXLoadingProcess from "../leyout/SizeSoftXLoadingProcess";
import { Fragment, useEffect, useState, useRef } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography, Paper, Box, Grid, Button } from "@mui/material";

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import PropTypes from "prop-types";
import { useLayout } from "../../context/System/LayoutContext";
import { useConfiguracion } from "../../context/System/ConfiguracionContext";

export default function SizeSoftXTableEtiquetado({
    data,
    columns,
    id,
    row,
    setRow,
    defaultRow,
    disableStickyHeader,
    fontSize = "medium",
    rowSpacing = 1,
    rowsPerPageIntro = 6,
    disablePagination = false,
    tableContainerRef,
    activaProgress,
    setClave,
    setMedida,

    // nuevos para captura de cantidades
    cantidades,              // { [idArticulo]: { [talla]: cantidad } }
    onChangeCantidad,        // (idArticulo, talla, value) => void
})
{
    const pager = [3, 6, 10, 20, 50];

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageIntro);
    const [dataRows, setDataRows] = useState(data);
    const [orderBy, setOrderBy] = useState("");
    const [order, setOrder] = useState(true);
    const [cargando, setCargando] = useState(false);

    const { tablaReferencia } = useLayout();
    const { setPaginadoModel, getPaginadoModel, paginadoModel } = useConfiguracion();
    
    const inputRefs = useRef({});
    const lastEditedRef = useRef(null);

    // Extraer todas las tallas únicas de todos los artículos - ordenar correctamente 22, 22.5, 23, 23.5...
    const tallas = Array.from(
        new Set(
            (data || [])
                .flatMap(a =>
                    (a.listamedidas || []).map(e => e.medida)
                )
        )
    ).sort((a, b) => {
        const numA = parseFloat(a);
        const numB = parseFloat(b);
        return numA - numB;
    });

    const handleClickOrder = (field) => {
        const isAsc = orderBy === field && order;
        const newOrder = !isAsc;

        const sortedData = [...dataRows].sort((a, b) => {
            let valueA = a[field] || "";
            let valueB = b[field] || "";

            return typeof valueA === "string"
                ? newOrder ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA)
                : newOrder ? valueA - valueB : valueB - valueA;
        });

        setDataRows(sortedData);
        setOrder(newOrder);
        setOrderBy(field);
    };

    const visibleRows = disablePagination
        ? dataRows
        : dataRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    const handleChangePage = (_, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Función para selección de filas
    const handleClickRow = (valueRow) => () => {
        try {
            if (!valueRow?.clavearticulo) return;

            setRow(valueRow);
            setClave(valueRow.clavearticulo);
        } catch {
            return;
        }
    };

    useEffect(() => {
        setDataRows(data);
        setPage(0);
    }, [data]);

    useEffect(() => {
        setRowsPerPage(rowsPerPageIntro);
    }, [rowsPerPageIntro]);

    useEffect(() => {
        setCargando(activaProgress);
    }, [activaProgress]);

    const getCantidad = (idArticulo, talla) =>
        cantidades?.[idArticulo]?.[talla] ?? "";

    const handleCantidadChange = (idArticulo, talla, raw) => {
        const value = raw === "" ? "" : Math.max(0, Number(raw) || 0);
        if (typeof onChangeCantidad === "function") {
            onChangeCantidad(idArticulo, talla, value);
        }
    };

    const getInputKey = (idArticulo, talla) => `${idArticulo}-${talla}`;
    
    const getAllInputKeys = () => {
        const keys = [];
        dataRows.forEach((articulo) => {
            const idArticulo = articulo[id];
            tallas.forEach((talla) => {
                const registro = (articulo.listamedidas || []).find(m => m.medida === talla);
                if (registro) {
                    keys.push(getInputKey(idArticulo, talla));
                }
            });
        });
        return keys;
    };

    // Rev 3: navegación por flechas en cuadrícula (sin stepper nativo: los
    // inputs son texto, ↑/↓/â†/→ solo mueven el foco). Tab/Enter intactos
    // (avance lineal con wrap). Los bordes envuelven: ↓ en último renglón va
    // al primero, ↑ en primero va al último; â†/→ igual dentro del renglón.
    // Las tallas no usadas (â—) siempre se saltan.
    const usaTalla = (articulo, talla) =>
        (articulo?.listamedidas || []).some(m => m.medida === talla);

    const enfocarInput = (idArticuloDestino, tallaDestino) => {
        const nextInput = inputRefs.current[getInputKey(idArticuloDestino, tallaDestino)];
        if (nextInput) {
            nextInput.focus();
            setTimeout(() => nextInput.select(), 0);
            return true;
        }
        return false;
    };

    const navegarCuadricula = (idArticulo, talla, dRow, dCol) => {
        const filas = Array.isArray(dataRows) ? dataRows : [];
        if (filas.length === 0) return;
        const rowIndex = filas.findIndex(a => a[id] === idArticulo);
        if (rowIndex === -1) return;
        const total = filas.length;

        if (dCol !== 0) {
            // Misma fila, talla anterior/siguiente con wrap (solo tallas usadas).
            const usadas = tallas.filter(t => usaTalla(filas[rowIndex], t));
            if (usadas.length === 0) return;
            let pos = usadas.indexOf(talla);
            if (pos === -1) pos = dCol > 0 ? -1 : 0;
            pos = (pos + dCol + usadas.length) % usadas.length;
            enfocarInput(filas[rowIndex][id], usadas[pos]);
            return;
        }

        // Misma talla, otro renglón con wrap (solo filas que usan la talla).
        let next = rowIndex;
        for (let paso = 0; paso < total; paso++) {
            next = (next + dRow + total) % total;
            if (usaTalla(filas[next], talla)) {
                enfocarInput(filas[next][id], talla);
                return;
            }
        }
    };

    const handleNavigation = (e, idArticulo, talla) => {
        const key = e.key;
        if (key === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
            navegarCuadricula(idArticulo, talla, 1, 0);
            return;
        }
        if (key === 'ArrowUp') {
            e.preventDefault();
            e.stopPropagation();
            navegarCuadricula(idArticulo, talla, -1, 0);
            return;
        }
        if (key === 'ArrowLeft') {
            e.preventDefault();
            e.stopPropagation();
            navegarCuadricula(idArticulo, talla, 0, -1);
            return;
        }
        if (key === 'ArrowRight') {
            e.preventDefault();
            e.stopPropagation();
            navegarCuadricula(idArticulo, talla, 0, 1);
            return;
        }
        if (key !== 'Tab' && key !== 'Enter') return;

        e.preventDefault();
        e.stopPropagation();

        const allKeys = getAllInputKeys();
        const currentKey = getInputKey(idArticulo, talla);
        let currentIndex = allKeys.indexOf(currentKey);

        if (currentIndex === -1) return;

        let nextIndex;

        if (e.shiftKey && key === 'Tab') {
            nextIndex = currentIndex - 1;
            if (nextIndex < 0) nextIndex = allKeys.length - 1;
        } else {
            nextIndex = currentIndex + 1;
            if (nextIndex >= allKeys.length) {
                nextIndex = 0;
            }
        }

        const nextKey = allKeys[nextIndex];
        const nextInput = inputRefs.current[nextKey];

        if (nextInput) {
            nextInput.focus();
            setTimeout(() => nextInput.select(), 0);
        }
    };

    const handleInputRef = (idArticulo, talla) => (el) => {
        if (el) {
            inputRefs.current[getInputKey(idArticulo, talla)] = el;
        }
    };

    return (
        <Fragment>
            <TableContainer
                component={Paper}
                ref={tableContainerRef}
                sx={{
                    // Red de seguridad: ningún input numérico dentro de esta
                    // tabla muestra spinners ni responde al stepper nativo.
                    '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                    '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                    '& input[type=number]': { MozAppearance: 'textfield', appearance: 'textfield' },
                }}
            >
                <Typography variant="h6" sx={{ m: .5 }}>
                    Etiquetado por Talla
                </Typography>
                <Table
                    size="small"
                    ref={tablaReferencia}
                    stickyHeader={!disableStickyHeader}
                    className="SizeSoftX-table"
                >
                    <TableHead>
                        <TableRow>
                            {columns.map((column, index) => (
                                <TableCell key={index}>
                                    {!column.hideSort ? (
                                        <Button
                                            variant="text"
                                            onClick={() => handleClickOrder(column.field)}
                                            startIcon={
                                                orderBy === column.field
                                                    ? (order ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />)
                                                    : null
                                            }
                                            sx={{ m: -2 }}
                                        >
                                            {column.title}
                                        </Button>
                                    ) : column.title}
                                </TableCell>
                            ))}
                            {tallas.map((talla) => (
                                <TableCell
                                    key={talla}
                                    align="center"
                                    sx={{
                                        padding: "2px 2px",
                                        fontSize: "0.75rem",
                                        minWidth: "30px",
                                        maxWidth: "60px",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {talla}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleRows
                            .map((articulo) => (
                                <TableRow
                                    key={articulo[id]}
                                    data-row-id={articulo[id]}
                                    tabIndex={-1}
                                    onClick={handleClickRow(articulo)}
                                    className={row && (row === articulo.clavearticulo || row[id] === articulo[id]) ? "row" : "no-row"}
                                >
                                    {columns.map((column, index) => (
                                        <TableCell
                                            key={index}
                                            sx={
                                                column.field === "descripcion"
                                                    ? {
                                                        minWidth: "320px",
                                                        whiteSpace: "normal",
                                                        wordWrap: "break-word",
                                                    }
                                                    : {
                                                        whiteSpace: "normal",
                                                        wordWrap: "break-word",
                                                    }
                                            }
                                        >
                                            {column.render
                                                ? column.render(articulo)
                                                : articulo[column.field]}
                                        </TableCell>
                                    ))}

                                    {tallas.map((talla) => {
                                        const registro = (articulo.listamedidas || [])
                                            .find(m => m.medida === talla);

                                        const idArticulo = articulo[id];

                                        //  Si el artículo NO usa esta talla, pintamos rojo y bloqueamos
                                        if (!registro) {
                                            return (
                                                <TableCell
                                                    key={talla}
                                                    align="center"
                                                    sx={{
                                                        padding: "2px 2px",
                                                        fontSize: "0.75rem",
                                                        minWidth: "40px",
                                                        maxWidth: "60px",
                                                        whiteSpace: "nowrap",
                                                        backgroundColor: "#ffebee", // rojo claro
                                                        color: "#b71c1c",            // rojo fuerte
                                                    }}
                                                >
                                                    {/* sólo indicador visual */}
                                                    â—
                                                </TableCell>
                                            );
                                        }

                                        // Si SÍ usa la talla, permitimos capturar cantidad
                                        return (
                                            <TableCell
                                                key={talla}
                                                align="center"
                                                onClick={(e) => {
                                                    e.stopPropagation();          // clave para evitar el doble onClick
                                                    if (!articulo?.clavearticulo) return;

                                                    setRow(articulo);
                                                    setClave(articulo.clavearticulo);
                                                    setMedida(talla);
                                                }}
                                                sx={{
                                                    padding: "2px 2px",
                                                    fontSize: "0.75rem",
                                                    minWidth: "40px",
                                                    maxWidth: "60px",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                <TextField
                                                    variant="standard"
                                                    type="text"
                                                    inputProps={{
                                                        inputMode: "numeric",
                                                        autoComplete: "off",
                                                        "aria-label": `Cantidad talla ${talla}`,
                                                        style: {
                                                            textAlign: "center",
                                                            fontSize: "0.75rem",
                                                            padding: 0,
                                                            maxWidth: "50px",
                                                        },
                                                    }}
                                                    value={getCantidad(idArticulo, talla)}
                                                    onChange={(e) =>
                                                        handleCantidadChange(
                                                            idArticulo,
                                                            talla,
                                                            e.target.value
                                                        )
                                                    }
                                                    onKeyDown={(e) => handleNavigation(e, idArticulo, talla)}
                                                    inputRef={handleInputRef(idArticulo, talla)}
                                                    onClick={(e) => e.target.select()}
                                                    onFocus={(e) => e.target.select()}
                                                />
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
                <SizeSoftXLoadingProcess
                    open={cargando}
                    message="Procesando información, por favor espere..."
                />
                {!disablePagination && (
                    <TablePagination
                        component="div"
                        count={dataRows.length}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={pager}
                        labelRowsPerPage="Registros por pégina"
                    />
                )}
            </TableContainer>
        </Fragment>
    );
}

SizeSoftXTableEtiquetado.propTypes = {
    columns: PropTypes.array.isRequired,
    data: PropTypes.array.isRequired,
    id: PropTypes.string.isRequired,
    row: PropTypes.object,
    setRow: PropTypes.func.isRequired,
    rowsPerPageIntro: PropTypes.number,
    disablePagination: PropTypes.bool,
    tableContainerRef: PropTypes.object,
    setClave: PropTypes.func,
    setMedida: PropTypes.func,
    cantidades: PropTypes.object,
    onChangeCantidad: PropTypes.func,
};
